import { useQueries } from "@tanstack/react-query";
import {
  endOfDay,
  endOfWeek,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
} from "date-fns";
import { useCallback, useEffect, useMemo } from "react";

import { plantaApi } from "../../api/fetch";
import { weekStartsOn$ } from "../dates/state";
import {
  periodBudget$,
  periodBudgetPerfectWeeksDates$,
  periodBudgetTotalWeeksDates$,
  totalPeriodPlan$,
} from "../period";
import { PLAN_QUERY_STALE_TIME_MS } from "../plan/usePlanMethods";
import { budgetData$, budgetStatus$ } from "../plan/state";
import {
  calculatePeriodHoursFromBudgetStatus,
  mapToBudgetStatus,
} from "./transforms";
import type {
  Budget,
  BudgetStatusArgs,
  CostCentreMapping,
  UseGetBudgetStatusReturn,
} from "./types";

export const useGetBudgetStatus = ({
  unitType,
  unitId,
  startDate,
  endDate,
}: BudgetStatusArgs): UseGetBudgetStatusReturn => {
  const isEnabled = Boolean(unitType?.trim() && unitId?.trim());

  const results = useQueries({
    queries: [
      {
        queryKey: [`hours-${unitType}-${unitId}-${startDate}-${endDate}`],
        queryFn: async () => {
          const hours = await plantaApi.jsonInJsonOut<ReadonlyArray<Budget>>(
            `/budgets/${unitType}/${unitId}?startDate=${startDate}&endDate=${endDate}`,
          );
          return hours;
        },
        enabled: isEnabled,
        staleTime: PLAN_QUERY_STALE_TIME_MS,
      },
      {
        queryKey: [
          `cost-centre-mappings-${unitType}-${unitId}-${startDate}-${endDate}`,
        ],
        queryFn: async () => {
          const mappings = await plantaApi.jsonInJsonOut<
            ReadonlyArray<CostCentreMapping>
          >(
            `/budgets/cost-centre-mappings/${unitType}/${unitId}?startDate=${startDate}&endDate=${endDate}`,
          );
          return mappings;
        },
        enabled: isEnabled,
        staleTime: PLAN_QUERY_STALE_TIME_MS,
      },
    ],
  });

  const [hoursResult, mappingsResult] = results;
  const hoursData = hoursResult.data;
  const mappingsData = mappingsResult.data;
  const isLoading = hoursResult.isLoading || mappingsResult.isLoading;

  const combinedData = useMemo(() => {
    if (!hoursData || !mappingsData) {
      return [];
    }

    const budgetStatus = mapToBudgetStatus(
      hoursData,
      mappingsData,
      totalPeriodPlan$.value,
    );

    return budgetStatus;
  }, [hoursData, mappingsData]);

  useEffect(() => {
    if (!hoursData || !mappingsData) {
      return;
    }

    const budgetStatus = mapToBudgetStatus(
      hoursData,
      mappingsData,
      totalPeriodPlan$.value,
    );

    budgetStatus$.value = budgetStatus;
    budgetData$.value = hoursData;

    const { min, max } = calculatePeriodHoursFromBudgetStatus(
      hoursData,
      endDate,
      startDate,
    );
    periodBudget$.value = { min, max };
    periodBudgetTotalWeeksDates$.value = [
      new Date(startDate),
      new Date(endDate),
    ];

    const eodPeriodEnd = endOfDay(new Date(endDate));
    const sodPeriodStart = startOfDay(new Date(startDate));

    // Find the first budget entry that contributes to min (has complete week in period)
    for (const item of hoursData) {
      const weekEnd = endOfWeek(new Date(item.startDate), {
        weekStartsOn: weekStartsOn$.value,
      });
      if (
        (isBefore(weekEnd, eodPeriodEnd) || isEqual(weekEnd, eodPeriodEnd)) &&
        (isAfter(new Date(item.startDate), new Date(sodPeriodStart)) ||
          isEqual(new Date(item.startDate), new Date(sodPeriodStart)))
      ) {
        periodBudgetPerfectWeeksDates$.value = [
          new Date(item.startDate),
          new Date(weekEnd),
        ];
        break;
      }
    }
  }, [hoursData, mappingsData, endDate, startDate]);

  const refetch = useCallback(() => {
    hoursResult.refetch();
    mappingsResult.refetch();
  }, [hoursResult, mappingsResult]);

  return {
    data: combinedData,
    rawBudgetData: hoursData,
    rawMappingsData: mappingsData,
    isLoading,
    refetch,
  };
};
